import { useEffect, useMemo } from 'react';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import {
  buildSerpApStockTransferContentHtml,
  SerpApStockTransferSnapshot,
} from '@/components/Approval/@components/SerpApStockTransfer';
import { getSerpApStockTransferDraftSourceUrl } from '@/constant/approval';
import { getNestedDataFetcher } from '@/utils/apis';
import type { SerpDocumentRef } from '@/utils/approval/serpDocument';
import type { SerpApStockTransferDraftSource } from '@/utils/approval/serpApStockTransfer';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

type SerpApStockTransferFieldProps = {
  /** 신규 작성은 진입 쿼리, 편집은 저장 attributes 의 SERP 문서 */
  serpDocument: SerpDocumentRef | null;
};

/**
 * SERP AP 재고 이관 요청(170) — 레거시 appr_edit_forms_serp_ap_inventory_transfer.
 * 편집도 SERP 를 다시 조회해 표를 새로 그림. 제목은 직접 입력(자동 입력 없음)
 */
const SerpApStockTransferField = ({
  serpDocument,
}: SerpApStockTransferFieldProps) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'serpApStockTransfer' });

  const typeCode = serpDocument?.typeCode;
  const sequence = serpDocument?.sequence;
  const { data, error, isLoading } = useSWR<SerpApStockTransferDraftSource>(
    typeCode && sequence
      ? getSerpApStockTransferDraftSourceUrl(typeCode, sequence)
      : null,
    getNestedDataFetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  useEffect(() => {
    const current = getValues('serpApStockTransfer');
    if (
      current &&
      (current.typeCode !== typeCode || current.sequence !== sequence)
    ) {
      setValue('serpApStockTransfer', null, { shouldDirty: false });
    }
  }, [typeCode, sequence, getValues, setValue]);

  useEffect(() => {
    if (!data || !typeCode || !sequence) return;
    if (
      data.attributes.typeCode !== typeCode ||
      data.attributes.sequence !== sequence
    )
      return;
    setValue(
      'serpApStockTransfer',
      { typeCode, sequence, source: data.display.source ?? {} },
      { shouldDirty: false },
    );
  }, [data, typeCode, sequence, setValue]);

  const content = useMemo(
    () =>
      state?.source
        ? buildSerpApStockTransferContentHtml(state.source, t)
        : null,
    [state?.source, t],
  );

  const renderBody = () => {
    if (!serpDocument) {
      return (
        <ApprovalNoticeBox role="alert">
          {t('SERP 문서 정보가 없습니다.')}
        </ApprovalNoticeBox>
      );
    }
    if (content) {
      return <SerpApStockTransferSnapshot content={content} />;
    }
    if (error) {
      return (
        <ApprovalNoticeBox role="alert">
          {t('데이터를 불러오지 못했습니다.')}
        </ApprovalNoticeBox>
      );
    }
    if (isLoading) {
      return (
        <ApprovalNoticeBox role="status">
          {t('데이터를 불러오는 중입니다...')}
        </ApprovalNoticeBox>
      );
    }
    return null;
  };

  return (
    <FieldBlock className="serp-ap-stock-transfer">{renderBody()}</FieldBlock>
  );
};

export default SerpApStockTransferField;
